import JsonResponse from "csi/comm/JsonResponse";
import ExtendedPath from "csi/core/ExtendedPath";
import { Field } from "csi/def/Field";
import DefRequest from "csi/def/Request";
import { getRelation } from "csi/reflection/reflection";
import { ICache, RemoveEventHandle } from "csi/typings";
import { getUrlPath } from "csi/utility/pathUtility";
import xml from "csi/xml";
import { CallMethod } from "csi/xml/CallMethod";
import Writer from "csi/xml/Writer";
import _ from "lodash";
import { isEditForm } from "src/c8-platform/service/formService";
import { IAggregateStoreResponse, IAggregateStoreUpdateOperation } from "src/c8-platform/type/aggregateStore";
import { IForm } from "src/c8-platform/type/form";
import { C8AttributeId, C8Node } from "src/c8-platform/type/node";
import { C8AttributeValue, C8FieldValue, VALUE_TYPE, attributeValueAsArray, cnl, getListValueType, getPrimitiveValueTypeIfContainer } from "src/c8-platform/type/value";

const { REF } = VALUE_TYPE;

/** write operation to copy the given value from a source attribute to multiple nodes */
export function writeCopyAggregateStoreValue(
	writer: Writer,
	updateOperation: IAggregateStoreUpdateOperation,
	sourceUrl: cnl,
	value: C8AttributeValue,
	targetUrls: cnl[],
): void {
	const { extraParams, module, operation } = updateOperation;

	// extract the type of the attribute being copied
	const paramsSet = _.keyBy(extraParams, (param) => param.id);
	const type = paramsSet["Type"].value.toString();

	// limit of 100 is somewhat arbitrary, but added just to ensure we don't trigger too many updates at once
	const call = writer.callList(module, operation, targetUrls, "TargetUrl", 100)
		.addParameter("SourceUrl", REF, sourceUrl)
		.addParameter("Value", type, value);

	extraParams.forEach(({ id, type, value }) => {
		call.addParameter(id, type, value);
	});
}

/** write operations to establish parent context of node in form */
export function writeFormParent(writer: Writer, formInfo: IForm): void {
	// if the node already exists, then it will already have its parent
	if (!isEditForm(formInfo)) {
		const { nodeUrl, parentAttributeId, parentAttributeKey, parentUrl } = formInfo;

		if (parentUrl) {
			writer.changeNode(nodeUrl).attach(parentUrl, parentAttributeId, parentAttributeKey);
		}
	}
}

export type WriterCallbackFunction = (response: JsonResponse) => void;

/** functions to call after any writer is submitted */
const callbacks = new Set<WriterCallbackFunction>();

/** register function to call when writer is executed, except when reporting */
export function registerWriterCallback(callback: WriterCallbackFunction): RemoveEventHandle {
	callbacks.add(callback);

	return {
		remove(): void {
			callbacks.delete(callback);
		},
	};
}

/** submit node operations in writer to server */
export async function submitWriter(writer: Writer): Promise<JsonResponse> {
	const response = await writer.execute();

	if (!writer.bReport) {
		for (const callback of callbacks) {
			callback(response);
		}
	}

	return response;
}

/**
 * Write operation to apply aggregate store operation
 * @param formParentUrl - indicates parent url to use when on form, and that form is being used in which case SetItems are passed rather than Add/RemoveItems
 */
export function writeAggregateStoreOperation(
	writer: Writer,
	aggregateStoreResponse: IAggregateStoreResponse,
	selectedValues: string[],
	removedValues?: string[],
	formParentUrl?: cnl,
): void {
	const {
		updateOperation: { extraParams, itemValueType, module, operation },
	} = aggregateStoreResponse;

	const call = writer.callMethod(module, operation);
	const valueType = getListValueType(itemValueType);

	// make sure to substitute real ParentURL for potentially fake client URL when creating/copying a node
	extraParams.forEach(({ id, type, value }) => {
		call.addParameter(id, type, (formParentUrl && (id === "ParentURL")) ? formParentUrl : value);
	});

	if (removedValues) {
		// if node already exists, then client knows exactly what is being added and removed
		call.addParameter("AddItems", valueType, selectedValues)
			.addParameter("RemoveItems", valueType, removedValues);
	} else {
		// if removedValues not provided, then let server determine if values need to be added or removed
		call.addParameter("SetItems", valueType, selectedValues);
	}
}

/** write operations for given fields if necessary; if form data has no node, then assume creating new node and process all fields */
export function writeFormFields(
	writer: Writer,
	urlNode: cnl,
	fields: Field[],
	detectChange = true,
	fieldFilterFunction: (field: Field) => boolean = Boolean,
	fieldValueFunction?: (field: Field) => C8FieldValue,
	fieldIdFunction?: (field: Field) => C8AttributeId,
): void {
	const { bReport } = writer;
	const delayedChanges: VoidFunction[] = [];

	fields = fields.filter((f) => !f.m_bUsed && !f.notApplicable && fieldFilterFunction(f));

	for (const f of fields) {
		const {
			aggregateStoreResponse,
			idParameter,
			m_path,
			m_rca,
			m_request,
		} = f;

		const bExpr = f.hasExpression();
		const bOneTime = bExpr && m_rca?.isOneTimeExp;
		const id = fieldIdFunction?.(f) ?? f.m_id;

		if (!detectChange || f.isUpdated()) {
			const url = getUrlPath(urlNode, m_path, true);
			let type = f.getType();
			let value = fieldValueFunction ? fieldValueFunction(f) : f.getValue();

			if (m_request) {
				delayedChanges.push(() => {
					writeRequest(writer, m_request)
						.addParameter(xml.PROP_URL, REF, url)
						.addParameter(idParameter || xml.PROP_VALUE, type, value);
				});
			} else if (id) {
				const attrType = m_rca?.valueType;
				if (attrType) {
					// if field is of primitive type of reflection attribute type, then convert value to an array
					if ((value != null) && !_.isArray(value) && (type === getPrimitiveValueTypeIfContainer(attrType))) {
						value = xml.valueIsValid(value, type) ? [value] : [];
						type = attrType;
					} else if (type !== attrType) {
						// assert that the type is correct
						// if reporting, then we will skip this field; the use case is normally a mass-create scenario where a "ref" attribute
						// is represented by a multi-select field resulting in a value that is an array
						// if not reporting, then let it proceed and generate an error - it should never happen, but may during development and
						// it is important to raise the server exception
						if (bReport) {
							continue;
						}

						console.warn("Field " + id + " has type " + type + " that does not match the model (" + attrType + ")");
					}
				}

				const cn = writer.changeNode(url);
				let restoredExpression = false;

				if (!bReport) {
					cn.changeAttributeFlag(id, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
				}

				if (bExpr) {
					if (f.bDisableExpressionEvaluation || !f.isDefaultValue()) {
						// if field was updated manually but has expression, or field expression is disabled altogether, then remove/disable the expression on server
						if (bOneTime) {
							cn.changeAttributeExp(id, "");
							cn.changeAttributeFlag(id, xml.ATTR_FLAG_EVAL_EXPRESSION | xml.ATTR_FLAG_ONE_TIME_EXPRESSION, false, true);
						} else {
							cn.disableAttributeExp(id);
						}
					} else if (!bOneTime && !f.bDisableExpressionEvaluation && f.startedWithDisabledContinuousExpression) {
						// if attribute's expression is disabled, but is no longer disabled on the field, then it must have been restored
						cn.restoreAttributeExp(id);
						restoredExpression = true;
					}
				}

				if (!restoredExpression) {
					// write change to attribute; if coming from aggregate store, then use operation specified in response;
					// make special exception for Subtype attribute as it must be specified as part of the CreateNode operation
					if (aggregateStoreResponse?.updateOperation.operationRequired) {
						delayedChanges.push(() => {
							writeAggregateStoreOperation(writer, aggregateStoreResponse, [].concat(value), null, url);
						});
					} else if (m_rca?.isReverseRelation) {
						value = attributeValueAsArray(value);

						value.filter(xml.isValidURL)
							.forEach((v) => {
								const { attributeId, valueType } = m_rca.bidirectionalRelation;
								if (valueType === VALUE_TYPE.REF) {
									writer.changeNode(v)
										.changeAttributeFlag(attributeId, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
										.changeAttribute(attributeId, valueType, url);
								} else {
									writer.changeNode(v)
										.changeAttributeFlag(attributeId, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
										.changeAttribute(attributeId, valueType, [url], true);
								}
							});
					} else {
						cn.changeAttribute(id, type, value);
					}
				}
			}
		} else if (bExpr && !bOneTime && !f.startedWithDisabledContinuousExpression) {
			// when editing a node, tell the server to evaluate continuous expressions that are not disabled just to make sure they are current
			writer.changeNode(getUrlPath(urlNode, m_path, true)).changeAttributeFlag(id, xml.ATTR_FLAG_EVAL_EXPRESSION, true);
		}
	}

	// apply custom operations to fields - we delay these until after all other changes so that as many changes as possible
	// are part of the initial CreateNode/ChangeNode operation
	delayedChanges.forEach((f) => f());
}

/** create network of nodes along given paths from given form node to simulate node graph that will be created by form */
export function writeFormPaths(writer: Writer, cache: ICache, formNode: C8Node, extendedPaths: ExtendedPath[], formToSourceNodeMap: Record<cnl, cnl> = {}): void {
	const processedUrls = new Set();
	let stop = false;

	function onStep(node: C8Node, nodePrevious: C8Node, attrId: string, isChild: boolean, index?: string | number): void {
		if (!node) {
			return;
		}

		const url = node.$URL;
		const previousUrl = nodePrevious.$URL;
		const sourceUrl = formToSourceNodeMap[url];

		if (stop || processedUrls.has(url)) {
			return;
		}

		processedUrls.add(url);

		if (!sourceUrl) {
			// if there is no source url for this node (client node), then write operation to create one
			writer.createNode(url, node.$Type);
		} else if (!formToSourceNodeMap[previousUrl]) {
			// if the node along path already exists, and the previous node did not, then just need to change one node to reference the other (below);
			// no need to process the path any further since have reached an existing node
			stop = true;
		} else {
			// both nodes already exist, so they already are related to one another
			return;
		}

		// attach url to previous url
		const rel = getRelation(nodePrevious, attrId);

		if (rel.isReverseRelation) {
			writer.changeNode(previousUrl).attach(url, rel.bidirectionalRelation.attributeId);
		} else {
			writer.changeNode(url).attach(previousUrl, attrId);
		}
	}

	extendedPaths.forEach((ep) => {
		cache.getPathNode(formNode, ep.toString(), 0, onStep);
		stop = false;
	});
}

/** write request */
export function writeRequest(writer: Writer, request: DefRequest, getParameterValue?: (id: string) => any): CallMethod {
	const { m_module, m_operation, m_parameters } = request;

	const call = writer.callMethod(m_module, m_operation);

	if (m_parameters) {
		for (const obj of m_parameters) {
			const value = obj.m_value ?? getParameterValue?.(obj.m_id);
			if (value != null) {
				call.addParameter(obj.m_id, obj.m_typeAttr, value);
			}
		}
	}

	return call;
}